// components/VersionBadge.tsx
/**
 * The build badge, bottom right on alpha and beta (never production). A button, not a hover
 * tooltip, so it works on a phone and from the keyboard: it opens a popover with the build, what
 * is new since the last release (CHANGELOG.md's Unreleased section) and the way to the style
 * sheet and the full changelog.
 */
import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Divider,
    Popover,
    type PopoverActions,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { colors, COLOUR_PREVIEWS, colourPreview, FONT_PREVIEWS, fontPreview } from '@/styles/base/tokens';
import { ChangelogReleaseView } from '@/components/common/Changelog';
import { useChangelog } from '@/hooks/useChangelog';
import { unreleasedOf } from '@/utils/changelog';

interface VersionBadgeProps {
    version?: string;
    environment?: string;
    buildDate?: string;
    gitBranch?: string;
    gitCommit?: string;
}

/**
 * Mounted only while the popover is open, so the changelog loads on first click. The popover
 * measured itself around the spinner, so it is told to measure again once the list is in, or
 * it runs off the bottom of the screen.
 */
const WhatsNew = ({ onLoaded }: { onLoaded: () => void }) => {
    const log = useChangelog();
    useEffect(() => {
        if (log) onLoaded();
    }, [log, onLoaded]);
    if (!log) return <CircularProgress size={16} aria-label="Loading what's new" />;
    const next = unreleasedOf(log);
    if (!next || next.groups.length === 0) {
        return (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Nothing listed since the last release.
            </Typography>
        );
    }
    return <ChangelogReleaseView release={next} compact />;
};

export const VersionBadge = ({ version, environment, buildDate, gitBranch, gitCommit }: VersionBadgeProps) => {
    const theme = useTheme();
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const popoverActions = useRef<PopoverActions>(null);
    const reposition = useRef(() => popoverActions.current?.updatePosition()).current;

    const getEnvColor = (): string => {
        switch (environment?.toLowerCase()) {
            case 'production':
            case 'live':
                return theme.palette.success.main;
            case 'alpha':
                return theme.palette.warning.main;
            case 'beta':
            case 'development':
                return theme.palette.info.main;
            default:
                return colors.grey[500];
        }
    };

    const getEnvDisplayText = (): string => {
        switch (environment?.toLowerCase()) {
            case 'production':
            case 'live':
                return 'PROD';
            case 'alpha':
                return 'ALPHA';
            case 'beta':
            case 'development':
                return 'DEV';
            default:
                return environment?.substring(0, 4) || 'DEV';
        }
    };

    if (!version && !environment) return null;

    const close = () => setAnchor(null);
    const facts: [string, string | undefined][] = [
        ['Environment', environment || 'Unknown'],
        ['Version', version || 'Unknown'],
        ['Built', buildDate],
        ['Branch', gitBranch],
        ['Commit', gitCommit],
        [
            'Colour preview',
            colourPreview !== 'default' ? COLOUR_PREVIEWS.find((p) => p.id === colourPreview)?.label : undefined,
        ],
        [
            'Typeface preview',
            fontPreview !== 'default' ? FONT_PREVIEWS.find((p) => p.id === fontPreview)?.label : undefined,
        ],
    ];

    return (
        <>
            <Box
                component="button"
                type="button"
                onClick={(e: React.MouseEvent<HTMLElement>) => setAnchor(e.currentTarget)}
                aria-haspopup="dialog"
                aria-expanded={Boolean(anchor)}
                aria-label="Build details and what's new"
                data-testid="version-badge"
                sx={{
                    position: 'fixed',
                    bottom: 8,
                    right: 8,
                    zIndex: 1800,
                    backgroundColor: 'rgba(0, 0, 0, 0.05)',
                    borderRadius: '4px',
                    padding: '2px 6px',
                    fontSize: '10px',
                    fontFamily: 'monospace',
                    fontWeight: 'bold',
                    color: getEnvColor(),
                    cursor: 'pointer',
                    opacity: anchor ? 1 : 0.7,
                    border: `1px solid ${getEnvColor()}`,
                    '&:hover, &:focus-visible': {
                        opacity: 1,
                        backgroundColor: 'rgba(0, 0, 0, 0.1)',
                    },
                }}
            >
                {getEnvDisplayText()} {gitCommit ? `• ${gitCommit}` : ''}
                {colourPreview !== 'default' || fontPreview !== 'default' ? ' • preview' : ''}
            </Box>
            <Popover
                action={popoverActions}
                open={Boolean(anchor)}
                anchorEl={anchor}
                onClose={close}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                slotProps={{
                    paper: {
                        role: 'dialog',
                        'aria-label': 'Build details and what is new',
                        // The build facts and the links stay put; only the list scrolls
                        sx: {
                            width: 420,
                            maxWidth: 'calc(100vw - 32px)',
                            maxHeight: '70vh',
                            p: 2,
                            mb: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                        },
                    },
                }}
                sx={{ zIndex: 1850 }}
            >
                <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 1.5 }}>
                    {facts
                        .filter(([, value]) => value)
                        .map(([label, value]) => (
                            <React.Fragment key={label}>
                                <Typography component="dt" variant="caption" sx={{ color: 'text.secondary' }}>
                                    {label}
                                </Typography>
                                <Typography component="dd" variant="caption" sx={{ m: 0, overflowWrap: 'anywhere' }}>
                                    {value}
                                </Typography>
                            </React.Fragment>
                        ))}
                </Box>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="subtitle2" component="h2" sx={{ mb: 0.5 }}>
                    New since the last release
                </Typography>
                <Box sx={{ overflowY: 'auto', minHeight: 0, flex: '1 1 auto', pr: 0.5 }}>
                    <WhatsNew onLoaded={reposition} />
                </Box>
                <Divider sx={{ my: 1.5 }} />
                <Stack direction="row" spacing={1}>
                    <Button size="small" component={RouterLink} to="/changelog" onClick={close}>
                        Full changelog
                    </Button>
                    <Button size="small" component={RouterLink} to="/style-sheet" onClick={close}>
                        Style sheet
                    </Button>
                </Stack>
            </Popover>
        </>
    );
};
